import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { login } from './authSlice';
import { AppDispatch, RootState } from '../../store/store';
import PWAInstallButton from '../../components/PWAInstallButton';

const Login = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [currentTime, setCurrentTime] = useState(new Date());
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const { loading, error } = useSelector((state: RootState) => state.auth);

    const from = (location.state as any)?.from?.pathname || '/';

    // Update time every second
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await dispatch(login({ username, password })).unwrap();
            navigate(from, { replace: true });
        } catch (err) {
            console.error('Login error:', err);
        }
    };

    const formatDateTime = (date: Date) => {
        const options: Intl.DateTimeFormatOptions = {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
        };
        return date.toLocaleString('en-US', options) + ' WIB';
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-500 to-blue-700 relative">
            <div className="bg-gray-800 p-12 rounded-2xl shadow-2xl w-[500px]">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center mb-4">
                        <div className="w-24 h-24 rounded-xl shadow-2xl bg-white p-2 flex items-center justify-center">
                            <img 
                                src="/icon-192x192.png" 
                                alt="GSP Logo" 
                                className="w-full h-full object-contain"
                                style={{ maxWidth: '100%', maxHeight: '100%' }}
                                onError={(e) => {
                                    console.error('Logo failed to load');
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.style.display = 'none';
                                    e.currentTarget.parentElement!.innerHTML = '<div class="text-yellow-600 text-xs">Logo Error</div>';
                                }}
                                onLoad={() => console.log('Logo loaded successfully')}
                            />
                        </div>
                    </div>
                    <h1 className="text-4xl font-bold text-white mb-2">
                        Gudang Sparepart Ponsel
                    </h1>
                    <p className="text-2xl text-gray-300 font-light">
                        POS System
                    </p>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-6">
                        {error}
                    </div>
                )}

                {/* Login Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Username */}
                    <div>
                        <label className="block text-white text-lg mb-2">
                            Username
                        </label>
                        <input
                            type="text"
                            placeholder="Enter your username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            required
                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Password */}
                    <div>
                        <label className="block text-white text-lg mb-2">
                            Password
                        </label>
                        <input
                            type="password"
                            placeholder="Enter your password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-4 pt-4">
                        {/* Login Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                        >
                            {loading ? 'Logging in...' : 'Login'}
                        </button>

                        {/* Guest Mode Button */}
                        <button
                            type="button"
                            onClick={() => navigate('/catalog')}
                            className="flex-1 bg-transparent hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg border-2 border-gray-500 hover:border-gray-400 transition text-lg"
                        >
                            Guest Mode
                        </button>
                    </div>

                    {/* PWA Install Button */}
                    <div className="pt-4 flex justify-center">
                        <PWAInstallButton variant="secondary" />
                    </div>
                </form>
            </div>

            {/* Footer with Time */}
            <div className="absolute bottom-8 right-8 text-white text-sm font-medium">
                {formatDateTime(currentTime)}
            </div>
        </div>
    );
};

export default Login;